<template>
	<div class="settings-wrapper">
		<BackToSettings />
		<div class="settings-content">
			<div class="settings-detail">
				<ProfilePicker
					class="profile-picker"
					:user="user"
					allowRemove
					size="90px"
					@success="onUploadSuccess"
					@remove="onRemovePic"
				/>

				<div class="role-tag">
					<Tag large v-if="['admin', 'support'].includes(user.role)" primary>
						{{ $t(`model_role_${user.role.toLowerCase()}`) }}
					</Tag>
				</div>

				<FormItem :label="$t('settings_edituser_firstname_placeholder')">
					<Input
						:placeholder="$t('settings_edituser_firstname_placeholder')"
						:disabled="!canChangeName"
						v-model="firstname"
					>
						<template #after v-if="!canChangeName">
							<LockKeyholeIcon />
						</template>
					</Input>
				</FormItem>

				<FormItem :label="$t('settings_edituser_lastname_placeholder')">
					<Input
						:placeholder="$t('settings_edituser_lastname_placeholder')"
						:disabled="!canChangeName"
						v-model="lastname"
					>
						<template #after v-if="!canChangeName">
							<LockKeyholeIcon />
						</template>
					</Input>
				</FormItem>

				<FormItem v-if="canChangePronouns" :label="$t('common_pronouns')">
					<Input
						:placeholder="$t('settings_profile_pronouns_placeholder')"
						:disabled="!canChangePronouns"
						v-model="pronouns"
						maxlength="40"
					>
						<template #after v-if="!canChangePronouns">
							<LockKeyholeIcon />
						</template>
					</Input>
				</FormItem>

				<FormItem v-if="canChangeTitle" :label="$t('common_title')">
					<Input
						:placeholder="$t('settings_profile_title_placeholder')"
						:disabled="!canChangeTitle"
						v-model="title"
						maxlength="40"
					>
						<template #after v-if="!canChangeTitle">
							<LockKeyholeIcon />
						</template>
					</Input>
				</FormItem>

				<FormItem v-if="phone" :label="$t('admin_people_phone_label')">
					<Input
						:placeholder="$t('settings_edituser_phone_placeholder')"
						disabled
						v-model="phone"
					>
						<template #after>
							<LockKeyholeIcon />
						</template>
					</Input>
				</FormItem>

				<FormItem v-if="email" :label="$t('admin_people_email_label')">
					<Input
						:placeholder="$t('settings_profile_title_placeholder')"
						disabled
						v-model="email"
					>
						<template #after>
							<LockKeyholeIcon />
						</template>
					</Input>
				</FormItem>

				<div class="locked-link">
					<LockKeyholeIcon />
					<div v-html="$t('settings_profile_locked')"></div>
				</div>

				<div class="button-wrapper">
					<Button
						large
						primary
						@click="update"
						:loading="isUpdating"
						:disabled="saveDisabled"
					>
						{{ $t('common_button_save') }}
					</Button>
				</div>
			</div>
		</div>
		<!-- DISCARD CHANGES MODAL -->
		<StandardModal
			v-if="continueToRoute"
			open
			small
			:title="$t('announcements_discard_changes')"
			:confirmText="$t('admin_common_discard_button_title')"
			:cancelText="$t('admin_common_keep_editing_button_title')"
			@confirm="onDiscardChanges"
			@cancel="continueToRoute = null"
		>
			<div class="discard-wrapper">
				<PencilIcon halo />
				<div v-t="'admin_common_discard_changes_confirm'" />
			</div>
		</StandardModal>
	</div>
</template>

<script>
import store from '@/store'
import {
	Tag,
	Button,
	ProfilePicker,
	Input,
	FormItem,
	StandardModal,
} from '@/components/common'
import { updateUser } from '@/api/user'
import BackToSettings from './BackToSettings'
import { LockKeyholeIcon, PencilIcon } from '@/icons'
import { USER_TITLE, USER_PRONOUNS, orgFlag } from '@/helpers/flags.js'

const MAX_NAME_LENGTH = 60

export default {
	name: 'ProfileForm',
	components: {
		Tag,
		Button,
		ProfilePicker,
		BackToSettings,
		Input,
		FormItem,
		LockKeyholeIcon,
		StandardModal,
		PencilIcon,
	},
	data() {
		const {
			firstname,
			lastname,
			phones,
			emails,
			pronouns,
			title,
		} = store.auth.user
		return {
			store,
			isUpdating: false,
			isLoading: false,
			firstname,
			lastname,
			phone: phones?.[0]?.phone ?? null,
			email: emails?.[0]?.email ?? null,
			pronouns,
			title,
			fileKey: null,
			shouldRemovePic: false,
			continueToRoute: null,
		}
	},

	beforeRouteLeave(to) {
		if (!this.saveDisabled) {
			this.continueToRoute = to
			return false
		}
	},

	computed: {
		user() {
			return this.store.auth.user
		},
		canChangeName() {
			return this.user.permissions.change_own_name
		},

		saveDisabled() {
			if (this.continueToRoute) return true
			if (this.shouldRemovePic) return false
			// Trim the input values to handle leading/trailing spaces
			const trimmedFirstName = this.firstname?.trim() || ''
			const trimmedLastName = this.lastname?.trim() || ''
			const trimmedTitle = this.title?.trim() || ''
			const trimmedPronouns = this.pronouns?.trim() || ''

			// Check if the basic name fields are unchanged
			const isNameUnchanged =
				trimmedFirstName === (this.user.firstname || '') &&
				trimmedLastName === (this.user.lastname || '')

			// Check if the new fields are unchanged
			const isTitleUnchanged = trimmedTitle === (this.user.title || '').trim()
			const isPronounUnchanged =
				trimmedPronouns === (this.user.pronouns || '').trim()

			// Never disable save if firstname and lastname are empty
			if (!trimmedFirstName || !trimmedLastName) {
				return true
			}

			// Disable save if all fields are unchanged
			if (
				isNameUnchanged &&
				isTitleUnchanged &&
				isPronounUnchanged &&
				!this.fileKey
			) {
				return true
			}

			// Enable save if any field is changed and has permission
			if (this.fileKey) {
				return false
			}

			if (this.canChangeName && !isNameUnchanged) {
				return false
			}
			if (this.canChangeTitle && !isTitleUnchanged) {
				return false
			}
			if (this.canChangePronouns && !isPronounUnchanged) {
				return false
			}

			// Default to disable save
			return true
		},

		canChangePronouns() {
			return orgFlag(USER_PRONOUNS) && this.user.permissions.change_own_pronouns
		},

		canChangeTitle() {
			return orgFlag(USER_TITLE) && this.user.permissions.change_own_title
		},
	},
	methods: {
		onDiscardChanges() {
			if (this.continueToRoute) {
				this.$router.push(this.continueToRoute)
			}
			this.$nextTick(() => {
				this.continueToRoute = null
			})
		},

		onUploadSuccess(upload) {
			this.fileKey = upload.key
			this.shouldRemovePic = false
		},

		onRemovePic() {
			this.fileKey = null
			this.shouldRemovePic = true
		},

		async update() {
			this.isUpdating = true

			let { firstname = '', lastname = '' } = this
			firstname = firstname.trim()
			lastname = lastname.trim()

			if (!firstname || !lastname) {
				this.isUpdating = false
				this.$alert.error(i18n.global.t('settings_edituser_name_missing'))
				return
			}

			if (
				firstname.length > MAX_NAME_LENGTH ||
				lastname.length > MAX_NAME_LENGTH
			) {
				this.isUpdating = false
				this.$alert.error(i18n.global.t('settings_edituser_name_maxlength'))
				return
			}

			const payload = {
				id: this.user.id,
				firstname,
				lastname,
			}

			if (this.canChangePronouns) {
				payload.pronouns = this.pronouns
			}

			if (this.canChangeTitle) {
				payload.title = this.title
			}
			if (this.shouldRemovePic) {
				payload.profilepic = null
			} else if (this.fileKey) {
				payload.profilepic = this.fileKey
			}

			try {
				const user = await updateUser(payload)
				this.firstname = user.firstname
				this.lastname = user.lastname
				this.fileKey = null

				this.isUpdating = false
				store.auth.setUser(user, store.userKey)
				this.$alert.success(this.$t('common_confirmation_updated'))
			} catch (error) {
				console.error(error)
				this.$alert.error(this.$t('common_api_error_save'))
				this.isUpdating = false
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.settings-wrapper {
	overflow-y: auto;
}

.settings-detail {
	.role-tag {
		@include flex-center;
		padding: 24px 0;
	}
	.profile-picker {
		margin: 0 auto;
	}
	.form-item {
		margin-bottom: 20px;
		.pronto-icon {
			color: var(--neutral-400);
			margin-right: 5px;
		}
	}

	.locked-link {
		color: var(--neutral-600);
		font-size: 10px;
		font-family: Avenir-Medium;
		display: flex;
		align-items: center;
		margin-top: 12px;

		.pronto-icon {
			margin-right: 5px;
			font-size: 16px;
			color: var(--neutral-400);
		}
	}

	.button-wrapper {
		display: flex;
		margin-top: 20px;
		.pronto-btn {
			margin-left: auto;
		}
	}
}

.discard-wrapper {
	@include flex-column-center;
	max-width: 300px;
	margin: 24px auto;
	text-align: center;
	.pronto-icon {
		color: var(--blue-200);
		font-size: 50px;
		margin-bottom: 24px;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.settings-detail {
	.locked-link a {
		color: var(--teal-300);
		text-decoration: none;
	}
}
</style>
